/**
 * Derived data (memoized): display waiters, filtered entries, daily/waiter totals, etc.
 */

import { useMemo } from 'react';
import { round2, waiterFullName } from '../utils/helpers';

export const useDerivedData = ({
  waiters,
  entries,
  closedDays,
  tipDate,
  fromDate,
  toDate,
  showInactive,
}) => {
  // Fast helpers
  const isDayClosed = (date) => !!closedDays[date];
  const getClosedMeta = (date) => closedDays[date] || null;

  const waiterById = useMemo(
    () => new Map(waiters.map((w) => [w.id, w])),
    [waiters],
  );

  const displayWaiters = useMemo(() => {
    const filtered = waiters.filter((w) => showInactive || w.status !== 'nieaktywny');
    return filtered.sort((a, b) => waiterFullName(a).localeCompare(waiterFullName(b), 'pl'));
  }, [waiters, showInactive]);

  const tipsWaiters = useMemo(
    () => displayWaiters.filter((w) => {
      const c = (w.category || '').toLowerCase().trim();
      // Akceptujemy obie formy: stanowisko ("kelner") i kategoria ("kelnerzy")
      return ['kelnerzy', 'kelner', 'kelnerka', 'kierownik', 'kierowniczka'].includes(c);
    }),
    [displayWaiters],
  );

  const dayEntries = useMemo(
    () => entries.filter((e) => e.date === tipDate),
    [entries, tipDate],
  );

  const dayTotals = useMemo(() => {
    let amount = 0, waiter = 0, kitchen = 0, bar = 0;
    for (const e of dayEntries) {
      amount += Number(e.amount || 0);
      waiter += Number(e.waiter || 0);
      kitchen += Number(e.kitchen || 0);
      bar += Number(e.bar || 0);
    }
    return {
      amount: round2(amount),
      waiter: round2(waiter),
      kitchen: round2(kitchen),
      bar: round2(bar),
      count: dayEntries.length,
    };
  }, [dayEntries]);

  const filteredEntries = useMemo(
    () =>
      entries.filter((e) => {
        if (fromDate && e.date < fromDate) return false;
        if (toDate && e.date > toDate) return false;
        return true;
      }),
    [entries, fromDate, toDate],
  );

  // Pure helper fns (no memo needed - cheap to redeclare and only used on demand)
  const overallTotals = (entryList) => {
    let amount = 0, waiter = 0, kitchen = 0, bar = 0;
    for (const e of entryList) {
      amount += Number(e.amount || 0);
      waiter += Number(e.waiter || 0);
      kitchen += Number(e.kitchen || 0);
      bar += Number(e.bar || 0);
    }
    return {
      amount: round2(amount),
      waiter: round2(waiter),
      kitchen: round2(kitchen),
      bar: round2(bar),
      count: entryList.length,
    };
  };

  const dailyTotals = (entryList) => {
    const m = new Map();
    for (const e of entryList) {
      if (!m.has(e.date)) m.set(e.date, { date: e.date, amount: 0, waiter: 0, kitchen: 0, count: 0 });
      const d = m.get(e.date);
      d.amount += Number(e.amount || 0);
      d.waiter += Number(e.waiter || 0);
      d.kitchen += Number(e.kitchen || 0);
      d.count += 1;
    }
    return [...m.values()]
      .map((x) => ({
        ...x,
        amount: round2(x.amount),
        waiter: round2(x.waiter),
        kitchen: round2(x.kitchen),
      }))
      .sort((a, b) => b.date.localeCompare(a.date));
  };

  const waiterTotals = (entryList) => {
    const m = new Map();
    for (const w of waiters) {
      m.set(w.id, { waiterId: w.id, amount: 0, waiter: 0, kitchen: 0, count: 0 });
    }
    for (const e of entryList) {
      if (!m.has(e.waiterId)) {
        m.set(e.waiterId, { waiterId: e.waiterId, amount: 0, waiter: 0, kitchen: 0, count: 0 });
      }
      const t = m.get(e.waiterId);
      t.amount += Number(e.amount || 0);
      t.waiter += Number(e.waiter || 0);
      t.kitchen += Number(e.kitchen || 0);
      t.count += 1;
    }
    return [...m.values()]
      .map((x) => ({
        ...x,
        amount: round2(x.amount),
        waiter: round2(x.waiter),
        kitchen: round2(x.kitchen),
      }))
      .sort((a, b) => b.amount - a.amount);
  };

  return {
    isDayClosed,
    getClosedMeta,
    waiterById,
    displayWaiters,
    tipsWaiters,
    dayEntries,
    dayTotals,
    filteredEntries,
    overallTotals,
    dailyTotals,
    waiterTotals,
  };
};
